import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import { getAdById } from '../../services/adsApi';
import type { Ad } from '../../types';
import styles from './MobileAdDetailPage.module.css';

export const MobileAdDetailPage: React.FC = () => {
  const { adId } = useParams<{ adId: string }>();
  const navigate = useNavigate();
  const [ad, setAd] = useState<Ad | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play carousel (10 seconds interval)
  useEffect(() => {
    if (!ad?.imageUrls || ad.imageUrls.length <= 1 || !isAutoPlaying) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev === ad.imageUrls!.length - 1 ? 0 : prev + 1
      );
    }, 10000);

    return () => clearInterval(interval);
  }, [ad, isAutoPlaying]);

  useEffect(() => {
    if (adId) {
      fetchAd(adId);
    }
  }, [adId]);

  const fetchAd = async (id: string) => {
    setLoading(true);
    try {
      const foundAd = await getAdById(id) as Ad | null;
      if (foundAd) {
        setAd(foundAd);
      } else {
        setAd(null);
      }
    } catch (error) {
      console.error('Error fetching ad:', error);
      setAd(null);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = () => {
    if (ad?.phone) {
      window.location.href = `tel:${ad.phone}`;
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: ad?.title || '分享廣告',
        url: window.location.href,
      });
    }
  };

  const handleImageChange = (direction: 'prev' | 'next') => {
    if (!ad?.imageUrls || ad.imageUrls.length === 0) return;

    // Reset auto-play timer when user manually changes image
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);

    if (direction === 'prev') {
      setCurrentImageIndex((prev) =>
        prev === 0 ? ad.imageUrls!.length - 1 : prev - 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === ad.imageUrls!.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Handle click on image - left side goes to prev, right side goes to next
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ad?.imageUrls || ad.imageUrls.length <= 1) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const midpoint = rect.width / 2;

    // Reset auto-play timer when user manually changes image
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);

    if (clickX < midpoint) {
      // Clicked on left side - go to previous
      setCurrentImageIndex((prev) =>
        prev === 0 ? ad.imageUrls!.length - 1 : prev - 1
      );
    } else {
      // Clicked on right side - go to next
      setCurrentImageIndex((prev) =>
        prev === ad.imageUrls!.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Touch handling for swipe gestures
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleImageChange('next');
    }
    if (isRightSwipe) {
      handleImageChange('prev');
    }
  };

  if (loading) {
    return (
      <div className={styles.mobileAdDetailPage}>
        <MobileHeader title="載入中..." />
        <div className={styles.mobileAdDetailPage__loading}>載入中...</div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className={styles.mobileAdDetailPage}>
        <MobileHeader title="廣告詳情" />
        <div className={styles.mobileAdDetailPage__error}>
          <p>找不到廣告資料</p>
          <button onClick={() => navigate(-1)}>返回</button>
        </div>
      </div>
    );
  }

  const images = ad.imageUrls && ad.imageUrls.length > 0 ? ad.imageUrls : [];
  const currentImage = images[currentImageIndex] || ad.imageUrls?.[0] || '';

  return (
    <div className={styles.mobileAdDetailPage}>
      <MobileHeader
        title="廣告詳情"
        semiTransparent
      />

      {/* Image Gallery */}
      <div className={styles.mobileAdDetailPage__gallery}>
        <div
          className={styles.mobileAdDetailPage__imageWrapper}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={handleImageClick}
        >
          <img
            src={currentImage}
            alt={ad.title}
            className={styles.mobileAdDetailPage__image}
          />
          {images.length > 1 && (
            <>
              {/* Arrow buttons are decorative only - use click/swipe on image for navigation */}
              <button
                className={styles.mobileAdDetailPage__imageNav}
                style={{ left: 8 }}
                type="button"
              >
                ‹
              </button>
              <button
                className={styles.mobileAdDetailPage__imageNav}
                style={{ right: 8 }}
                type="button"
              >
                ›
              </button>
              <div className={styles.mobileAdDetailPage__imageCounter}>
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.mobileAdDetailPage__content}>
        {/* Price and Promo */}
        {ad.specialOffer && (
          <div className={styles.mobileAdDetailPage__promo}>
            <span className={styles.mobileAdDetailPage__promoText}>
              {ad.specialOffer}
            </span>
          </div>
        )}

        {/* Title */}
        <h1 className={styles.mobileAdDetailPage__title}>{ad.title}</h1>

        {/* Summary */}
        {ad.description && (
          <p className={styles.mobileAdDetailPage__summary}>{ad.description}</p>
        )}

        {/* Store Info */}
        <div className={styles.mobileAdDetailPage__storeCard}>
          <div className={styles.mobileAdDetailPage__storeHeader}>
            <div className={styles.mobileAdDetailPage__storeInfo}>
              <span className={styles.mobileAdDetailPage__storeName}>
                {ad.storeName}
              </span>
              {ad.featured && (
                <span className={styles.mobileAdDetailPage__verifiedBadge}>
                  ✓ 已認證
                </span>
              )}
            </div>
            {/* TODO: 關注功能預計於未來版本開放，目前先隱藏按鈕 */}
            {/* <button className={styles.mobileAdDetailPage__followBtn}>
              + 關注
            </button> */}
          </div>
          <div className={styles.mobileAdDetailPage__storeStats}>
            <span>⭐ 4.7 分</span>
            <span>📦 {ad.viewCount}+ 瀏覽</span>
          </div>
        </div>

        {/* Contact Info */}
        <div className={styles.mobileAdDetailPage__section}>
          <h3 className={styles.mobileAdDetailPage__sectionTitle}>聯絡資訊</h3>
          {ad.phone && (
            <div className={styles.mobileAdDetailPage__contactItem}>
              <span className={styles.mobileAdDetailPage__contactIcon}>📞</span>
              <span>{ad.phone}</span>
            </div>
          )}
          {ad.contactEmail && (
            <div className={styles.mobileAdDetailPage__contactItem}>
              <span className={styles.mobileAdDetailPage__contactIcon}>✉️</span>
              <span>{ad.contactEmail}</span>
            </div>
          )}
          {ad.address && (
            <div className={styles.mobileAdDetailPage__contactItem}>
              <span className={styles.mobileAdDetailPage__contactIcon}>📍</span>
              <span>{ad.address}</span>
            </div>
          )}
          {ad.businessHours && (
            <div className={styles.mobileAdDetailPage__contactItem}>
              <span className={styles.mobileAdDetailPage__contactIcon}>🕐</span>
              <span>{ad.businessHours}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {ad.description && (
          <div className={styles.mobileAdDetailPage__section}>
            <h3 className={styles.mobileAdDetailPage__sectionTitle}>服務詳情</h3>
            <div className={styles.mobileAdDetailPage__description}>
              {ad.description}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className={styles.mobileAdDetailPage__section}>
          <h3 className={styles.mobileAdDetailPage__sectionTitle}>服務統計</h3>
          <div className={styles.mobileAdDetailPage__stats}>
            <div className={styles.mobileAdDetailPage__statItem}>
              <span className={styles.mobileAdDetailPage__statValue}>
                {ad.viewCount}
              </span>
              <span className={styles.mobileAdDetailPage__statLabel}>瀏覽次數</span>
            </div>
            <div className={styles.mobileAdDetailPage__statItem}>
              <span className={styles.mobileAdDetailPage__statValue}>
                {ad.clickCount}
              </span>
              <span className={styles.mobileAdDetailPage__statLabel}>點擊次數</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className={styles.mobileAdDetailPage__actions}>
        <button
          className={styles.mobileAdDetailPage__actionButton}
          onClick={handleShare}
        >
          <span>📤</span>
          <span>分享</span>
        </button>
        <button
          className={`${styles.mobileAdDetailPage__actionButton} ${styles['mobileAdDetailPage__actionButton--primary']}`}
          onClick={handleContact}
        >
          <span>📞</span>
          <span>立即聯絡</span>
        </button>
      </div>
    </div>
  );
};

export default MobileAdDetailPage;
